import React, { useEffect, useMemo, useRef, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import MasterV2 from "../MasterV2";

import {
  LoadingDiv,
  LoadingDivShort,
  SweetAlertShow,
} from "../../master/Master";

import { courseList } from "../../../api/Courses";
import { batchList } from "../../../api/Batch";
import { StaffList } from "../../../api/Staff";

import { studentAttendanceList } from "../../../api/Attendance";

import { formatToTime } from "../../master/FormatToDateTime";

/* =========================================================
   HELPERS
========================================================= */

const getToday = () => {
  const date = new Date();

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatReadableDate = (value) => {
  if (!value) return "-";

  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

const getInitials = (name) => {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word?.[0]?.toUpperCase())
    .join("");
};

const escapePrintHtml = (value) => {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const validAttendanceValue = (value) => {
  return ["", "0", "1", "2"].includes(String(value ?? ""))
    ? String(value ?? "")
    : "";
};

/* =========================================================
   PAGE
========================================================= */

export default function DayOverviewReport() {
  const navigate = useNavigate();
  const location = useLocation();

  const firstLoadRef = useRef(true);

  /* =======================================================
     READ URL PARAMETERS
  ======================================================= */

  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const urlSessionId = urlParams.get("session_id") || "";

  const urlDate = urlParams.get("date") || "";

  const urlAttendance = urlParams.has("attendance")
    ? validAttendanceValue(urlParams.get("attendance"))
    : "";

  const urlCourseId = urlParams.get("course_id") || "";

  const urlBatchId = urlParams.get("batch_id") || "";

  const urlStaffId = urlParams.get("staff_id") || "";

  /*
   * If page came from dashboard like:
   *
   * ?session_id=9
   * &date=2026-09-30
   * &attendance=1
   *
   * batch_id is not supplied.
   *
   * Therefore batch_scope = "all"
   */
  const initialBatchScope = urlBatchId ? "single" : "all";

  /* =======================================================
     STATE
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [filterLoading, setFilterLoading] = useState(false);

  const [sessions, setSessions] = useState([]);

  const [courses, setCourses] = useState([]);

  const [batches, setBatches] = useState([]);

  const [staffs, setStaffs] = useState([]);

  const [data, setData] = useState({});

  const [search, setSearch] = useState("");

  const [printMenuOpen, setPrintMenuOpen] = useState(false);

  /*
   * Report View:
   * "students" = student-wise attendance list
   * "batches"  = batch-wise attendance summary
   */
  const [reportView, setReportView] = useState("students");

  /*
   * batch_scope:
   *
   * "select" = Select Batch
   * "all"    = All Batches
   * "single" = Specific batch
   */

  const [batchScope, setBatchScope] = useState(initialBatchScope);

  /*
   * URL IS USED HERE.
   *
   * Example:
   *
   * /attendance/reports/day-wise
   * ?session_id=9
   * &date=2026-09-30
   * &attendance=1
   *
   * will initialize:
   *
   * session_id = 9
   * date = 2026-09-30
   * attendance = 1
   * batch = All Batches
   */

  const [filters, setFilters] = useState({
    session_id: urlSessionId,

    course_id: urlCourseId,

    batch_id: urlBatchId,

    staff_id: urlStaffId,

    date: urlDate || getToday(),

    attendance: urlParams.has("attendance") ? urlAttendance : "2",
  });

  /* =======================================================
     LOAD SESSIONS
  ======================================================= */

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("sessions") ?? "[]");

      setSessions(Array.isArray(stored) ? stored : []);
    } catch (error) {
      console.error("Session parsing error:", error);

      setSessions([]);
    }
  }, []);

  /* =======================================================
     ACTIVE SESSION FALLBACK

     URL session always has priority.
  ======================================================= */

  useEffect(() => {
    if (!sessions.length) {
      return;
    }

    /*
     * Already initialized from URL.
     */
    if (filters.session_id) {
      return;
    }

    const activeSession = sessions.find((item) => Number(item?.status) === 1);

    if (!activeSession?.id) {
      return;
    }

    setFilters((previous) => ({
      ...previous,

      session_id: String(activeSession.id),
    }));
  }, [sessions, filters.session_id]);

  /* =======================================================
     COURSES
  ======================================================= */

  const getCourses = async () => {
    try {
      setFilterLoading(true);

      const response = await courseList();

      if (response?.data?.status) {
        setCourses(
          Array.isArray(response?.data?.data) ? response.data.data : [],
        );
      } else {
        setCourses([]);
      }
    } catch (error) {
      console.error("Course loading error:", error);

      setCourses([]);
    } finally {
      setFilterLoading(false);
    }
  };

  /* =======================================================
     TEACHERS / STAFF

     Same API concept as your old attendance page.
  ======================================================= */

  const getStaffs = async () => {
    try {
      const response = await StaffList("status=1");

      if (response?.data?.status) {
        setStaffs(
          Array.isArray(response?.data?.data) ? response.data.data : [],
        );
      } else {
        setStaffs([]);
      }
    } catch (error) {
      console.error("Staff loading error:", error);

      setStaffs([]);
    }
  };

  useEffect(() => {
    getCourses();
    getStaffs();
  }, []);

  /* =======================================================
     BATCHES

     IMPORTANT:
     Course is NOT compulsory.

     This means:
     Session selected
        ↓
     API gets all active batches

     Course selected
        ↓
     API gets batches of that course.
  ======================================================= */

  const getBatches = async () => {
    if (!filters.session_id) {
      setBatches([]);
      return;
    }

    try {
      setFilterLoading(true);

      let addUrl = "&status=1";

      addUrl += `&session_id=${encodeURIComponent(filters.session_id)}`;

      if (filters.course_id) {
        addUrl += `&course_id=${encodeURIComponent(filters.course_id)}`;
      }

      /*
       * Optional teacher filter for batch
       * API only if your batch endpoint
       * supports staff_id.
       *
       * We don't add staff_id here because
       * attendance API already handles it.
       */

      const response = await batchList(addUrl);

      if (response?.data?.status) {
        setBatches(
          Array.isArray(response?.data?.data) ? response.data.data : [],
        );
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error("Batch loading error:", error);

      setBatches([]);
    } finally {
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    getBatches();

    // eslint-disable-next-line
  }, [filters.session_id, filters.course_id]);

  /* =======================================================
     BUILD ATTENDANCE QUERY
  ======================================================= */

  const buildAttendanceQuery = (custom = {}) => {
    const finalFilters = {
      ...filters,
      ...custom,
    };

    /*
     * ALL BATCHES
     *
     * Do NOT send batch_id.
     */
    if (batchScope === "all") {
      delete finalFilters.batch_id;
    }

    /*
     * Select Batch placeholder.
     *
     * Also do not send batch_id.
     */
    if (batchScope === "select") {
      delete finalFilters.batch_id;
    }

    let addUrl = "";

    Object.entries(finalFilters).forEach(([key, value]) => {
      /*
       * IMPORTANT:
       *
       * attendance=0 is valid.
       *
       * Therefore don't use:
       *
       * if (value)
       */

      if (value !== undefined && value !== null && String(value) !== "") {
        addUrl += `&${key}=${encodeURIComponent(value)}`;
      }
    });

    return addUrl;
  };

  /* =======================================================
     GET ATTENDANCE
  ======================================================= */

  const getAttendanceReport = async () => {
    if (!filters.session_id || !filters.date) {
      return;
    }

    try {
      setLoading(true);

      const addUrl = buildAttendanceQuery();

      console.log("Attendance API Filters:", addUrl);

      const response = await studentAttendanceList(addUrl);

      if (response?.data?.status) {
        setData(response?.data?.data || {});
      } else {
        setData({});
      }
    } catch (error) {
      console.error("Attendance report error:", error);

      setData({});

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load attendance report.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     AUTO LOAD

     Now ALL BATCHES is valid.
     We do NOT require course/batch.
  ======================================================= */

  useEffect(() => {
    if (!filters.session_id || !filters.date) {
      return;
    }

    getAttendanceReport();

    // eslint-disable-next-line
  }, [
    filters.session_id,
    filters.course_id,
    filters.batch_id,
    filters.staff_id,
    filters.date,
    filters.attendance,
    batchScope,
  ]);

  /* =======================================================
     KEEP URL UPDATED

     Makes filters bookmarkable and dashboard-friendly.
  ======================================================= */

  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;

      return;
    }

    if (!filters.session_id) {
      return;
    }

    const params = new URLSearchParams();

    params.set("session_id", filters.session_id);

    params.set("date", filters.date);

    /*
     * attendance="" means All.
     * Keep it explicitly so refresh
     * restores same report.
     */

    params.set("attendance", filters.attendance);

    if (filters.course_id) {
      params.set("course_id", filters.course_id);
    }

    /*
     * Only put batch_id into URL
     * when a specific batch is selected.
     */

    if (batchScope === "single" && filters.batch_id) {
      params.set("batch_id", filters.batch_id);
    }

    if (filters.staff_id) {
      params.set("staff_id", filters.staff_id);
    }

    navigate(
      {
        pathname: location.pathname,

        search: `?${params.toString()}`,
      },
      {
        replace: true,
      },
    );

    // eslint-disable-next-line
  }, [
    filters.session_id,
    filters.course_id,
    filters.batch_id,
    filters.staff_id,
    filters.date,
    filters.attendance,
    batchScope,
  ]);

  /* =======================================================
     RESPONSE
  ======================================================= */

  const rows = Array.isArray(data?.data) ? data.data : [];

  const totalCount = Number(data?.total || 0);

  const presentCount = Number(data?.present || 0);

  const absentCount = Number(data?.absent || 0);

  const notMarkedCount = Number(data?.no_mark ?? data?.not_marked ?? 0);

  /* =======================================================
     PERCENTAGE
  ======================================================= */

  const getPercentage = (value) => {
    if (!totalCount) {
      return 0;
    }

    return Number(((Number(value) / totalCount) * 100).toFixed(1));
  };

  const presentPercentage = getPercentage(presentCount);

  const absentPercentage = getPercentage(absentCount);

  const notMarkedPercentage = getPercentage(notMarkedCount);

  /* =======================================================
     SELECTED OBJECTS
  ======================================================= */

  const selectedSession = sessions.find(
    (item) => String(item?.id) === String(filters.session_id),
  );

  const selectedCourse = courses.find(
    (item) => String(item?.id) === String(filters.course_id),
  );

  const selectedBatch = batches.find(
    (item) => String(item?.id) === String(filters.batch_id),
  );

  const selectedStaff = staffs.find(
    (item) => String(item?.id) === String(filters.staff_id),
  );

  /* =======================================================
     SEARCH
  ======================================================= */

  const filteredRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      return rows;
    }

    return rows.filter((item) => {
      const courseTitle = item?.latest_course_and_fee?.course?.title || "";

      const batchTitle = item?.latest_course_and_fee?.batch?.title || "";

      const searchable = [
        item?.name,
        item?.registration_no,
        item?.phone,
        item?.mobile,
        courseTitle,
        batchTitle,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(keyword);
    });
  }, [rows, search]);

  /* =======================================================
     BATCH WISE REPORT
  ======================================================= */

  const batchWiseRows = useMemo(() => {
    const grouped = new Map();

    rows.forEach((item) => {
      const feeInfo = item?.latest_course_and_fee || {};
      const batch = feeInfo?.batch || {};
      const course = feeInfo?.course || {};

      const batchId =
        batch?.id ??
        feeInfo?.batch_id ??
        item?.batch_id ??
        `unknown-${course?.id || "course"}`;

      const batchName =
        batch?.title ||
        item?.batch_title ||
        item?.batch_name ||
        "Unassigned Batch";

      const courseName =
        course?.title || item?.course_title || item?.course_name || "-";

      if (!grouped.has(String(batchId))) {
        grouped.set(String(batchId), {
          batch_id: batchId,
          batch_name: batchName,
          course_name: courseName,
          total: 0,
          present: 0,
          absent: 0,
          not_marked: 0,
        });
      }

      const group = grouped.get(String(batchId));
      const checkIn = Number(item?.today_attendance?.check_in);

      group.total += 1;

      if (checkIn === 1) {
        group.present += 1;
      } else if (checkIn === 2) {
        group.absent += 1;
      } else {
        group.not_marked += 1;
      }
    });

    return Array.from(grouped.values())
      .map((item) => ({
        ...item,
        percentage:
          item.total > 0
            ? Number(((item.present / item.total) * 100).toFixed(1))
            : 0,
      }))
      .sort((a, b) =>
        String(a.batch_name || "").localeCompare(String(b.batch_name || "")),
      );
  }, [rows]);

  const filteredBatchWiseRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      return batchWiseRows;
    }

    return batchWiseRows.filter((item) =>
      [item?.batch_name, item?.course_name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(keyword),
    );
  }, [batchWiseRows, search]);

  const changeReportView = (view) => {
    setReportView(view);
    setSearch("");
    setPrintMenuOpen(false);

    /*
     * Batch report needs all attendance statuses so its
     * Total / Present / Absent / Not Marked values are correct.
     */
    if (view === "batches" && filters.attendance !== "") {
      setFilters((previous) => ({
        ...previous,
        attendance: "",
      }));
    }
  };

  /* =======================================================
     STATUS
  ======================================================= */

  const getAttendanceStatus = (item) => {
    const checkIn = Number(item?.today_attendance?.check_in);

    if (checkIn === 1) {
      return {
        key: "present",
        label: "Present",
        icon: "ri-checkbox-circle-line",
        className: "attendance-status-present",
      };
    }

    if (checkIn === 2) {
      return {
        key: "absent",
        label: "Absent",
        icon: "ri-close-circle-line",
        className: "attendance-status-absent",
      };
    }

    return {
      key: "not_marked",
      label: "Not Marked",
      icon: "ri-time-line",
      className: "attendance-status-pending",
    };
  };

  /* =======================================================
     TIME
  ======================================================= */

  const getInTime = (item) => {
    const attendance = item?.today_attendance;

    const value =
      attendance?.in_time ??
      attendance?.check_in_time ??
      attendance?.punch_in ??
      null;

    if (!value) {
      return "-";
    }

    try {
      return formatToTime(value);
    } catch {
      return value;
    }
  };

  const getOutTime = (item) => {
    const attendance = item?.today_attendance;

    const value =
      attendance?.out_time ??
      attendance?.check_out_time ??
      attendance?.punch_out ??
      null;

    if (!value) {
      return "-";
    }

    try {
      return formatToTime(value);
    } catch {
      return value;
    }
  };

  /* =======================================================
     SUMMARY CARDS
  ======================================================= */

  const summaryCards = [
    {
      key: "",
      title: "Total Enrolled",
      value: totalCount,
      description: "All students",
      icon: "ri-group-line",
      type: "primary",
    },

    {
      key: "1",
      title: "Present",
      value: presentCount,
      description: `${presentPercentage}% present`,
      icon: "ri-checkbox-circle-line",
      type: "success",
    },

    {
      key: "2",
      title: "Absent",
      value: absentCount,
      description: `${absentPercentage}% absent`,
      icon: "ri-close-circle-line",
      type: "danger",
    },

    {
      key: "0",
      title: "Not Marked",
      value: notMarkedCount,
      description: `${notMarkedPercentage}% pending`,
      icon: "ri-time-line",
      type: "warning",
    },
  ];

  /* =======================================================
     ATTENDANCE FILTER

     Clicking dashboard cards inside this
     page also automatically uses All Batches.
  ======================================================= */

  const changeAttendanceFilter = (value) => {
    setSearch("");

    /*
     * You requested:
     *
     * click Present / Absent etc.
     * -> show all batches by default.
     */

    setBatchScope("all");

    setFilters((previous) => ({
      ...previous,

      attendance: value,

      batch_id: "",
    }));
  };

  /* =======================================================
     BATCH CHANGE
  ======================================================= */

  const handleBatchChange = (event) => {
    const value = event.target.value;

    /*
     * Placeholder
     */
    if (value === "__select__") {
      setBatchScope("select");

      setFilters((previous) => ({
        ...previous,
        batch_id: "",
      }));

      return;
    }

    /*
     * All Batches
     */
    if (value === "__all__") {
      setBatchScope("all");

      setFilters((previous) => ({
        ...previous,
        batch_id: "",
      }));

      return;
    }

    /*
     * Specific batch
     */
    setBatchScope("single");

    setFilters((previous) => ({
      ...previous,
      batch_id: value,
    }));
  };

  const batchSelectValue =
    batchScope === "all"
      ? "__all__"
      : batchScope === "select"
        ? "__select__"
        : filters.batch_id || "__select__";

  /* =======================================================
     RESET
  ======================================================= */

  const resetFilters = () => {
    setSearch("");

    setBatchScope("all");

    const activeSession = sessions.find((item) => Number(item?.status) === 1);

    setFilters({
      session_id: activeSession?.id ? String(activeSession.id) : "",

      course_id: "",
      batch_id: "",
      staff_id: "",

      date: getToday(),

      attendance: "2",
    });
  };

  /* =======================================================
     TITLE
  ======================================================= */

  const currentAttendanceTitle =
    filters.attendance === "1"
      ? "Present Students"
      : filters.attendance === "2"
        ? "Absent Students"
        : filters.attendance === "0"
          ? "Not Marked Students"
          : "All Students";

  /* =========================================================
     PRINT
  ========================================================= */

  const openPrintWindow = ({ title, content, landscape = true }) => {
    const printWindow = window.open("", "_blank", "width=1200,height=850");

    if (!printWindow) {
      SweetAlertShow(
        "info",
        "Popup Blocked",
        "Please allow popups and try again.",
      );

      return;
    }

    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>

        <meta charset="UTF-8" />

        <title>
          ${escapePrintHtml(title)}
        </title>

        <style>

          @page {
            size: A4 ${landscape ? "landscape" : "portrait"};
            margin: 10mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            font-family:
              Arial,
              sans-serif;

            color: #222;

            font-size: 11px;

            -webkit-print-color-adjust:
              exact;

            print-color-adjust:
              exact;
          }

          h1 {
            margin: 0;
            font-size: 20px;
          }

          .subtitle {
            margin-top: 4px;
            color: #666;
            font-size: 11px;
          }

          .header {
            padding-bottom: 10px;
            margin-bottom: 10px;
            border-bottom:
              2px solid #333;
          }

          .meta {
            display: grid;
            grid-template-columns:
              repeat(5, 1fr);

            gap: 6px;

            margin-bottom: 12px;
          }

          .meta-box {
            padding: 7px;
            border:
              1px solid #ccc;
          }

          .meta-box span {
            display: block;

            margin-bottom: 3px;

            color: #777;

            font-size: 8px;

            text-transform:
              uppercase;
          }

          .meta-box strong {
            font-size: 10px;
          }

          .summary {
            display: grid;

            grid-template-columns:
              repeat(4, 1fr);

            gap: 8px;

            margin-bottom: 12px;
          }

          .summary > div {
            padding: 9px;

            border:
              1px solid #ccc;

            text-align: center;
          }

          .summary span {
            display: block;

            font-size: 9px;

            color: #666;
          }

          .summary strong {
            display: block;

            margin-top: 4px;

            font-size: 20px;
          }

          table {
            width: 100%;

            border-collapse:
              collapse;
          }

          thead {
            display:
              table-header-group;
          }

          tr {
            page-break-inside:
              avoid;
          }

          th {
            padding: 6px;

            border:
              1px solid #777;

            background: #eee;

            font-size: 9px;

            text-align: left;
          }

          td {
            padding: 6px;

            border:
              1px solid #aaa;

            font-size: 9px;
          }

          .center {
            text-align: center;
          }

          .present {
            color: #18864b;
            font-weight: bold;
          }

          .absent {
            color: #c93636;
            font-weight: bold;
          }

          .pending {
            color: #bd7315;
            font-weight: bold;
          }

          .footer {
            margin-top: 10px;

            padding-top: 6px;

            border-top:
              1px solid #ccc;

            color: #777;

            font-size: 8px;

            text-align: right;
          }

        </style>

      </head>

      <body>

        ${content}

        <script>

          window.onload =
            function () {

              setTimeout(
                function () {

                  window.focus();
                  window.print();

                },
                300
              );

            };

          window.onafterprint =
            function () {
              window.close();
            };

        </script>

      </body>

      </html>
    `);

    printWindow.document.close();
  };

  const getPrintMeta = () => {
    return `
      <div class="meta">

        <div class="meta-box">
          <span>Date</span>
          <strong>
            ${escapePrintHtml(formatReadableDate(filters.date))}
          </strong>
        </div>

        <div class="meta-box">
          <span>Session</span>
          <strong>
            ${escapePrintHtml(selectedSession?.session || "-")}
          </strong>
        </div>

        <div class="meta-box">
          <span>Course</span>
          <strong>
            ${escapePrintHtml(selectedCourse?.title || "All Courses")}
          </strong>
        </div>

        <div class="meta-box">
          <span>Batch</span>
          <strong>
            ${escapePrintHtml(
              batchScope === "all"
                ? "All Batches"
                : selectedBatch?.title || "Not Selected",
            )}
          </strong>
        </div>

        <div class="meta-box">
          <span>Teacher</span>
          <strong>
            ${escapePrintHtml(selectedStaff?.name || "All Teachers")}
          </strong>
        </div>

      </div>
    `;
  };

  const getPrintSummary = () => {
    return `
      <div class="summary">

        <div>
          <span>Total</span>
          <strong>
            ${totalCount}
          </strong>
        </div>

        <div>
          <span>Present</span>
          <strong>
            ${presentCount}
          </strong>
        </div>

        <div>
          <span>Absent</span>
          <strong>
            ${absentCount}
          </strong>
        </div>

        <div>
          <span>Not Marked</span>
          <strong>
            ${notMarkedCount}
          </strong>
        </div>

      </div>
    `;
  };

  const printStudentTable = () => {
    setPrintMenuOpen(false);

    if (!filteredRows.length) {
      SweetAlertShow(
        "info",
        "No Students",
        "There are no students available to print.",
      );

      return;
    }

    const bodyRows = filteredRows
      .map((item, index) => {
        const status = getAttendanceStatus(item);

        const course = item?.latest_course_and_fee?.course?.title || "-";

        const batch = item?.latest_course_and_fee?.batch?.title || "-";

        const statusClass =
          status.key === "present"
            ? "present"
            : status.key === "absent"
              ? "absent"
              : "pending";

        return `
      <tr>
        <td class="center">${index + 1}</td>

        <td>${escapePrintHtml(item?.name || "-")}</td>

        <td>${escapePrintHtml(item?.registration_no || "-")}</td>

        <td>${escapePrintHtml(item?.phone || "-")}</td>

        <td>${escapePrintHtml(item?.secondary_mobile || "-")}</td>

        <td>${escapePrintHtml(course)}</td>

        <td>${escapePrintHtml(batch)}</td>

        <td class="center ${statusClass}">
          ${escapePrintHtml(status.label)}
        </td>

        <td class="center">
          ${escapePrintHtml(getInTime(item))}
        </td>

        <td class="center">
          ${escapePrintHtml(getOutTime(item))}
        </td>
      </tr>
    `;
      })
      .join("");

    const content = `

      <div class="header">

        <h1>
          Day Wise Attendance Report
        </h1>

        <div class="subtitle">
          ${escapePrintHtml(currentAttendanceTitle)}
        </div>

      </div>

      ${getPrintMeta()}

      <table>

        <thead>

          <tr>

            <th class="center">
              #
            </th>

            <th>
              Student
            </th>

            <th>
              Registration No.
            </th>
            <th>Phone</th>
    <th>Secondary Mobile</th>
            <th>
              Course
            </th>

            <th>
              Batch
            </th>

            <th class="center">
              Attendance
            </th>

            <th class="center">
              IN
            </th>

            <th class="center">
              OUT
            </th>

          </tr>

        </thead>

        <tbody>
          ${bodyRows}
        </tbody>

      </table>

      <div class="footer">

        Total Printed:
        ${filteredRows.length}

        &nbsp; | &nbsp;

        ${escapePrintHtml(new Date().toLocaleString("en-IN"))}

      </div>
    `;

    openPrintWindow({
      title: currentAttendanceTitle,

      content,

      landscape: true,
    });
  };

  const printAttendanceSummary = () => {
    setPrintMenuOpen(false);

    const content = `

        <div class="header">

          <h1>
            Daily Attendance Summary
          </h1>

          <div class="subtitle">
            ${escapePrintHtml(formatReadableDate(filters.date))}
          </div>

        </div>

        ${getPrintMeta()}

        ${getPrintSummary()}

        <table>

          <thead>

            <tr>

              <th>
                Status
              </th>

              <th class="center">
                Students
              </th>

              <th class="center">
                Percentage
              </th>

            </tr>

          </thead>

          <tbody>

            <tr>
              <td>
                Total Enrolled
              </td>

              <td class="center">
                ${totalCount}
              </td>

              <td class="center">
                100%
              </td>
            </tr>

            <tr>
              <td>
                Present
              </td>

              <td
                class="
                  center present
                "
              >
                ${presentCount}
              </td>

              <td class="center">
                ${presentPercentage}%
              </td>
            </tr>

            <tr>
              <td>
                Absent
              </td>

              <td
                class="
                  center absent
                "
              >
                ${absentCount}
              </td>

              <td class="center">
                ${absentPercentage}%
              </td>
            </tr>

            <tr>
              <td>
                Not Marked
              </td>

              <td
                class="
                  center pending
                "
              >
                ${notMarkedCount}
              </td>

              <td class="center">
                ${notMarkedPercentage}%
              </td>
            </tr>

          </tbody>

        </table>
      `;

    openPrintWindow({
      title: "Daily Attendance Summary",

      content,

      landscape: false,
    });
  };

  const printBatchTable = () => {
    setPrintMenuOpen(false);

    if (!filteredBatchWiseRows.length) {
      SweetAlertShow(
        "info",
        "No Batches",
        "There are no batches available to print.",
      );
      return;
    }

    const bodyRows = filteredBatchWiseRows
      .map(
        (item, index) => `
          <tr>
            <td class="center">${index + 1}</td>
            <td>${escapePrintHtml(item?.batch_name || "-")}</td>
            <td>${escapePrintHtml(item?.course_name || "-")}</td>
            <td class="center">${item?.total || 0}</td>
            <td class="center present">${item?.present || 0}</td>
            <td class="center absent">${item?.absent || 0}</td>
            <td class="center pending">${item?.not_marked || 0}</td>
            <td class="center"><strong>${item?.percentage || 0}%</strong></td>
          </tr>
        `,
      )
      .join("");

    const content = `
      <div class="header">
        <h1>Batch Wise Attendance Report</h1>
        <div class="subtitle">
          ${escapePrintHtml(formatReadableDate(filters.date))}
        </div>
      </div>

      ${getPrintMeta()}
      ${getPrintSummary()}

      <table>
        <thead>
          <tr>
            <th class="center">#</th>
            <th>Batch</th>
            <th>Course</th>
            <th class="center">Total</th>
            <th class="center">Present</th>
            <th class="center">Absent</th>
            <th class="center">Not Marked</th>
            <th class="center">Attendance %</th>
          </tr>
        </thead>
        <tbody>
          ${bodyRows}
        </tbody>
      </table>

      <div class="footer">
        Generated from Student Attendance V2
      </div>
    `;

    openPrintWindow({
      title: "Batch Wise Attendance Report",
      content,
      landscape: true,
    });
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar attendance-dashboard day-attendance-page">
        <div className="layout-container">
          {/* SIDEBAR */}
          <div className="no-print">
            <Header />
          </div>

          <div className="layout-page">
            {/* TOP NAVBAR */}
            <div className="no-print">
              <Nav />
            </div>

            {/* FIXED APPLICATION CONTENT WRAPPER */}
            <div className="content-wrapper day-content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y day-content-container">
                {/* ATTENDANCE MASTER NAVIGATION */}
                <div className="no-print day-master-wrapper">
                  <MasterV2 />
                </div>

                <main className="">
                  {/* HEADER */}

                  {/* <section className="day-report-header">
                    <div className="day-header-content">
                      <button
                        type="button"
                        className="day-back-btn"
                        onClick={() => navigate(-1)}
                      >
                        <i className="ri-arrow-left-line" />
                      </button>

                      <div className="day-header-icon">
                        <i className="ri-calendar-check-line" />
                      </div>

                      <div>
                        <div className="day-breadcrumb">
                          Attendance Reports
                          <i className="ri-arrow-right-s-line" />
                          Day Wise
                        </div>

                        <h1>Day Wise Attendance Report</h1>

                        <p>
                          View student attendance by date, batch and assigned
                          class teacher.
                        </p>
                      </div>
                    </div>

                    <div className="day-header-actions">
                      <button
                        type="button"
                        className="day-btn day-btn-light"
                        onClick={getAttendanceReport}
                      >
                        <i className="ri-refresh-line" />
                        Refresh
                      </button>

                      <div className="day-print-wrapper">
                        <button
                          type="button"
                          className="day-btn day-btn-primary"
                          onClick={() =>
                            setPrintMenuOpen((previous) => !previous)
                          }
                        >
                          <i className="ri-printer-line" />
                          Print
                          <i className="ri-arrow-down-s-line" />
                        </button>

                        {printMenuOpen && (
                          <div className="day-print-menu">
                            <button type="button" onClick={printStudentTable}>
                              <i className="ri-table-line" />

                              <span>
                                <strong>Student Table</strong>

                                <small>Print filtered student list</small>
                              </span>
                            </button>

                            <button
                              type="button"
                              onClick={printAttendanceSummary}
                            >
                              <i className="ri-bar-chart-box-line" />

                              <span>
                                <strong>Attendance Summary</strong>

                                <small>Print totals and percentages</small>
                              </span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </section> */}

                  {/* REPORT VIEW SWITCH */}

                  {/* <section className="day-view-switch-card no-print">
                    <div className="day-view-switch-copy">
                      <span className="day-view-switch-icon">
                        <i className="ri-file-chart-line" />
                      </span>

                      <div>
                        <h2>Attendance Report View</h2>
                        <p>
                          Choose student-wise details or a complete batch-wise
                          attendance summary for the selected date.
                        </p>
                      </div>
                    </div>

                    <div className="day-view-switch">
                      <button
                        type="button"
                        className={reportView === "students" ? "active" : ""}
                        onClick={() => changeReportView("students")}
                      >
                        <i className="ri-user-line" />
                        <span>
                          <strong>Students Report</strong>
                          <small>Student attendance list</small>
                        </span>
                      </button>

                      <button
                        type="button"
                        className={reportView === "batches" ? "active" : ""}
                        onClick={() => changeReportView("batches")}
                      >
                        <i className="ri-stack-line" />
                        <span>
                          <strong>Batches Report</strong>
                          <small>Batch attendance summary</small>
                        </span>
                      </button>
                    </div>
                  </section> */}

                  {/* URL NOTICE */}

                  {(urlDate || urlParams.has("attendance")) && (
                    <div className="day-url-report-notice">
                      <i className="ri-link" />

                      <div>
                        <strong>Dashboard Report Loaded</strong>

                        <span>
                          Showing {currentAttendanceTitle.toLowerCase()} for{" "}
                          {formatReadableDate(filters.date)} across{" "}
                          {batchScope === "all"
                            ? "all batches"
                            : selectedBatch?.title || "selected batch"}
                          .
                        </span>
                      </div>
                    </div>
                  )}

                  {/* FILTERS */}

                  <section className="day-filter-card">
                    <div className="day-card-heading">
                      <div>
                        <h2>
                          <i className="ri-filter-3-line" />
                          Report Filters
                        </h2>

                        <p>
                          Filter attendance by session, date, course, batch or
                          class teacher.
                        </p>
                      </div>

                      <div className="day-header-actions">
                        <button
                          type="button"
                          className="day-reset-btn"
                          onClick={resetFilters}
                        >
                          <i className="ri-restart-line" />
                          Reset
                        </button>
                        <button
                          type="button"
                          className="day-btn day-btn-light"
                          onClick={getAttendanceReport}
                        >
                          <i className="ri-refresh-line" />
                          Refresh
                        </button>

                        <div className="day-print-wrapper">
                          <button
                            type="button"
                            className="day-btn day-btn-primary"
                            onClick={() =>
                              setPrintMenuOpen((previous) => !previous)
                            }
                          >
                            <i className="ri-printer-line" />
                            Print
                            <i className="ri-arrow-down-s-line" />
                          </button>

                          {printMenuOpen && (
                            <div className="day-print-menu">
                              <button type="button" onClick={printStudentTable}>
                                <i className="ri-table-line" />

                                <span>
                                  <strong>Student Table</strong>

                                  <small>Print filtered student list</small>
                                </span>
                              </button>

                              <button type="button" onClick={printBatchTable}>
                                <i className="ri-stack-line" />

                                <span>
                                  <strong>Batch Wise Report</strong>

                                  <small>Print batch attendance summary</small>
                                </span>
                              </button>

                              <button
                                type="button"
                                onClick={printAttendanceSummary}
                              >
                                <i className="ri-bar-chart-box-line" />

                                <span>
                                  <strong>Attendance Summary</strong>

                                  <small>Print totals and percentages</small>
                                </span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="day-filter-grid">
                      {/* SESSION */}

                      <div className="day-field">
                        <label>Academic Session</label>

                        <div className="day-control">
                          <i className="ri-graduation-cap-line" />

                          <select
                            value={filters.session_id}
                            onChange={(e) => {
                              setBatchScope("all");

                              setFilters((previous) => ({
                                ...previous,

                                session_id: e.target.value,

                                course_id: "",

                                batch_id: "",
                              }));
                            }}
                          >
                            <option value="">Select Session</option>

                            {sessions.map((session) => (
                              <option key={session.id} value={session.id}>
                                {session.session}

                                {Number(session.status) === 1
                                  ? " - Active"
                                  : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* DATE */}

                      <div className="day-field">
                        <label>Attendance Date</label>

                        <div className="day-control">
                          <i className="ri-calendar-line" />

                          <input
                            type="date"
                            value={filters.date}
                            onChange={(e) =>
                              setFilters((previous) => ({
                                ...previous,

                                date: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      {/* COURSE */}

                      <div className="day-field">
                        <label>Course</label>

                        <div className="day-control">
                          <i className="ri-book-open-line" />

                          <select
                            value={filters.course_id}
                            onChange={(e) => {
                              setBatchScope("all");

                              setFilters((previous) => ({
                                ...previous,

                                course_id: e.target.value,

                                batch_id: "",
                              }));
                            }}
                          >
                            <option value="">All Courses</option>

                            {courses.map((course) => (
                              <option key={course.id} value={course.id}>
                                {course.title}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* BATCH */}

                      <div className="day-field">
                        <label>Batch</label>

                        <div className="day-control">
                          <i className="ri-group-2-line" />

                          <select
                            value={batchSelectValue}
                            onChange={handleBatchChange}
                          >
                            <option value="__select__" disabled>
                              Select Batch
                            </option>

                            <option value="__all__">All Batches</option>

                            {batches.map((batch) => (
                              <option key={batch.id} value={batch.id}>
                                {batch.title}
                              </option>
                            ))}
                          </select>
                        </div>

                        <small className="day-field-help">
                          {batchScope === "all"
                            ? `All ${batches.length} available batches included`
                            : selectedBatch?.title
                              ? `Only ${selectedBatch.title}`
                              : "Choose a batch"}
                        </small>
                      </div>

                      {/* TEACHER */}

                      <div className="day-field">
                        <label>Assigned Teacher</label>

                        <div className="day-control">
                          <i className="ri-user-star-line" />

                          <select
                            value={filters.staff_id}
                            onChange={(e) =>
                              setFilters((previous) => ({
                                ...previous,

                                staff_id: e.target.value,
                              }))
                            }
                          >
                            <option value="">All Teachers</option>

                            {staffs.map((staff) => (
                              <option key={staff.id} value={staff.id}>
                                {staff.name}

                                {staff.registration_no
                                  ? ` (${staff.registration_no})`
                                  : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {filterLoading && (
                      <div className="day-filter-loading">
                        <i className="ri-loader-4-line day-spin" />
                        Loading filter options...
                      </div>
                    )}
                  </section>

                  {/* ACTIVE FILTERS */}

                  <div className="day-active-filters">
                    <span>
                      <i className="ri-calendar-line" />
                      {formatReadableDate(filters.date)}
                    </span>

                    <span>
                      <i className="ri-group-2-line" />

                      {batchScope === "all"
                        ? "All Batches"
                        : selectedBatch?.title || "Batch"}
                    </span>

                    {selectedStaff && (
                      <span>
                        <i className="ri-user-star-line" />
                        {selectedStaff.name}
                      </span>
                    )}

                    <span className="active-status">
                      <i className="ri-filter-line" />
                      {currentAttendanceTitle}
                    </span>
                  </div>

                  {/* SUMMARY */}

                  <section className="day-summary-grid">
                    {summaryCards.map((card) => {
                      const active = filters.attendance === card.key;

                      return (
                        <button
                          type="button"
                          key={card.title}
                          className={
                            `day-summary-card ${card.type} ` +
                            (active ? "active" : "")
                          }
                          onClick={() => changeAttendanceFilter(card.key)}
                        >
                          <div className="day-summary-header">
                            <div className="day-summary-icon">
                              <i className={card.icon} />
                            </div>

                            {active && (
                              <span className="day-selected-badge">
                                Selected
                              </span>
                            )}
                          </div>

                          <strong className="day-summary-value">
                            {loading ? "..." : card.value}
                          </strong>

                          <h3>{card.title}</h3>

                          <p>{card.description}</p>
                        </button>
                      );
                    })}
                  </section>

                  {/* STUDENTS */}
                  <div className="day-view-switch">
                    <button
                      type="button"
                      className={reportView === "students" ? "active" : ""}
                      onClick={() => changeReportView("students")}
                    >
                      <i className="ri-user-line" />
                      <span>
                        <strong>Students Report</strong>
                        <small>Student attendance list</small>
                      </span>
                    </button>

                    <button
                      type="button"
                      className={reportView === "batches" ? "active" : ""}
                      onClick={() => changeReportView("batches")}
                    >
                      <i className="ri-stack-line" />
                      <span>
                        <strong>Batches Report</strong>
                        <small>Batch attendance summary</small>
                      </span>
                    </button>
                  </div>
                  {reportView === "students" && (
                    <section className="day-table-card">
                      <div className="day-table-header">
                        <div>
                          <h2>{currentAttendanceTitle}</h2>

                          <p>
                            {filteredRows.length} student
                            {filteredRows.length === 1 ? "" : "s"} found
                            {batchScope === "all"
                              ? " across all batches"
                              : selectedBatch?.title
                                ? ` in ${selectedBatch.title}`
                                : ""}
                          </p>
                        </div>

                        <div className="day-table-actions">
                          <div className="day-mini-select">
                            <i className="ri-filter-line" />

                            <select
                              value={filters.attendance}
                              onChange={(e) =>
                                changeAttendanceFilter(e.target.value)
                              }
                            >
                              <option value="">All Students</option>

                              <option value="1">Present</option>

                              <option value="2">Absent</option>

                              <option value="0">Not Marked</option>
                            </select>
                          </div>

                          <div className="day-search">
                            <i className="ri-search-line" />

                            <input
                              type="text"
                              value={search}
                              placeholder="Search name, reg. no., phone, batch..."
                              onChange={(e) => setSearch(e.target.value)}
                            />

                            {search && (
                              <button
                                type="button"
                                onClick={() => setSearch("")}
                              >
                                <i className="ri-close-line" />
                              </button>
                            )}
                          </div>

                          <button
                            type="button"
                            className="day-table-print"
                            onClick={printStudentTable}
                          >
                            <i className="ri-printer-line" />
                            Print Table
                          </button>
                        </div>
                      </div>

                      <div className="day-table-scroll">
                        <table className="day-table">
                          <thead>
                            <tr>
                              <th className="number">#</th>

                              <th>Student</th>

                              <th>Registration No.</th>

                              <th>Course</th>

                              <th>Batch</th>

                              <th>Attendance</th>

                              <th>IN Time</th>

                              <th>OUT Time</th>
                            </tr>
                          </thead>

                          <tbody>
                            {!loading && filteredRows.length === 0 && (
                              <tr>
                                <td colSpan="8" className="day-empty">
                                  <i className="ri-user-search-line" />

                                  <strong>No Students Found</strong>

                                  <span>
                                    No students match the selected filters.
                                  </span>
                                </td>
                              </tr>
                            )}

                            {filteredRows.map((item, index) => {
                              const status = getAttendanceStatus(item);

                              const course =
                                item?.latest_course_and_fee?.course?.title ||
                                "-";

                              const batch =
                                item?.latest_course_and_fee?.batch?.title ||
                                "-";

                              return (
                                <tr key={item?.id || index}>
                                  <td className="number">{index + 1}</td>

                                  <td>
                                    <div className="day-student">
                                      <div className="day-avatar">
                                        {getInitials(item?.name)}
                                      </div>

                                      <div>
                                        <strong>{item?.name || "-"}</strong>

                                        {(item?.phone || item?.mobile) && (
                                          <span>
                                            {item?.phone || item?.mobile}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>

                                  <td>
                                    <span className="day-reg">
                                      {item?.registration_no || "-"}
                                    </span>
                                  </td>

                                  <td>{course}</td>

                                  <td>
                                    <span className="day-batch">{batch}</span>
                                  </td>

                                  <td>
                                    <span
                                      className={
                                        `day-status ` + status.className
                                      }
                                    >
                                      <i className={status.icon} />

                                      {status.label}
                                    </span>
                                  </td>

                                  <td>{getInTime(item)}</td>

                                  <td>{getOutTime(item)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  )}

                  {/* BATCH WISE REPORT */}

                  {reportView === "batches" && (
                    <section className="day-table-card">
                      <div className="day-table-header">
                        <div>
                          <h2>Batch Wise Attendance</h2>

                          <p>
                            {filteredBatchWiseRows.length} batch
                            {filteredBatchWiseRows.length === 1
                              ? ""
                              : "es"}{" "}
                            found for {formatReadableDate(filters.date)}
                          </p>
                        </div>

                        <div className="day-table-actions">
                          <div className="day-search">
                            <i className="ri-search-line" />

                            <input
                              type="text"
                              value={search}
                              placeholder="Search batch or course..."
                              onChange={(e) => setSearch(e.target.value)}
                            />

                            {search && (
                              <button
                                type="button"
                                onClick={() => setSearch("")}
                              >
                                <i className="ri-close-line" />
                              </button>
                            )}
                          </div>

                          <button
                            type="button"
                            className="day-table-print"
                            onClick={printBatchTable}
                          >
                            <i className="ri-printer-line" />
                            Print Batches
                          </button>
                        </div>
                      </div>

                      <div className="day-table-scroll">
                        <table className="day-table day-batch-report-table">
                          <thead>
                            <tr>
                              <th className="number">#</th>
                              <th>Batch</th>
                              <th>Course</th>
                              <th className="text-center">Total</th>
                              <th className="text-center">Present</th>
                              <th className="text-center">Absent</th>
                              <th className="text-center">Not Marked</th>
                              <th>Attendance</th>
                              <th className="no-print">Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            {!loading && filteredBatchWiseRows.length === 0 && (
                              <tr>
                                <td colSpan="9" className="day-empty">
                                  <i className="ri-stack-line" />
                                  <strong>No Batches Found</strong>
                                  <span>
                                    No batch attendance is available for the
                                    selected filters.
                                  </span>
                                </td>
                              </tr>
                            )}

                            {filteredBatchWiseRows.map((item, index) => {
                              const percentage = Number(item?.percentage || 0);

                              return (
                                <tr key={item?.batch_id || index}>
                                  <td className="number">{index + 1}</td>

                                  <td>
                                    <div className="day-batch-name-cell">
                                      <span className="day-batch-icon">
                                        <i className="ri-group-2-line" />
                                      </span>

                                      <div>
                                        <strong>
                                          {item?.batch_name || "-"}
                                        </strong>
                                        <small>
                                          {item?.total || 0} enrolled student
                                          {Number(item?.total || 0) === 1
                                            ? ""
                                            : "s"}
                                        </small>
                                      </div>
                                    </div>
                                  </td>

                                  <td>{item?.course_name || "-"}</td>

                                  <td className="text-center">
                                    <span className="day-count-badge total">
                                      {item?.total || 0}
                                    </span>
                                  </td>

                                  <td className="text-center">
                                    <span className="day-count-badge present">
                                      {item?.present || 0}
                                    </span>
                                  </td>

                                  <td className="text-center">
                                    <span className="day-count-badge absent">
                                      {item?.absent || 0}
                                    </span>
                                  </td>

                                  <td className="text-center">
                                    <span className="day-count-badge pending">
                                      {item?.not_marked || 0}
                                    </span>
                                  </td>

                                  <td>
                                    <div className="day-batch-progress">
                                      <div className="day-batch-progress-top">
                                        <strong>{percentage}%</strong>
                                        <span>
                                          {percentage >= 75
                                            ? "Good"
                                            : percentage >= 50
                                              ? "Average"
                                              : "Low"}
                                        </span>
                                      </div>

                                      <div className="day-batch-progress-track">
                                        <span
                                          className={
                                            percentage >= 75
                                              ? "good"
                                              : percentage >= 50
                                                ? "average"
                                                : "low"
                                          }
                                          style={{
                                            width: `${Math.min(
                                              Math.max(percentage, 0),
                                              100,
                                            )}%`,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </td>

                                  <td className="no-print">
                                    <button
                                      type="button"
                                      className="day-view-students-btn"
                                      onClick={() => {
                                        setReportView("students");
                                        setBatchScope("single");
                                        setSearch("");
                                        setFilters((previous) => ({
                                          ...previous,
                                          batch_id: String(
                                            item?.batch_id || "",
                                          ),
                                          attendance: "",
                                        }));
                                      }}
                                    >
                                      <i className="ri-user-search-line" />
                                      View Students
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  )}
                </main>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===================================================
          STYLES
      =================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }

        .day-attendance-page {
          min-height: 100vh;
          background: #f6f7fb;
          color: #454954;
        }

        .day-attendance-page .layout-container {
          min-height: 100vh;
        }

        .day-attendance-page .layout-page {
          min-width: 0;
          min-height: 100vh;
        }

        .day-content-wrapper {
          width: 100%;
          min-width: 0;
          background: #f6f7fb;
        }

        .day-content-container {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          padding-top: 16px;
          padding-right: 18px;
          padding-bottom: 40px;
          padding-left: 18px;
        }

        .day-master-wrapper {
          width: 100%;
          min-width: 0;
          margin-bottom: 18px;
        }

        .day-report-main {
          width: 100%;
          max-width: 1600px;
          min-width: 0;
          margin: 0 auto;
          padding: 0;
        }

        /* ================================
           REPORT VIEW SWITCH
        ================================ */

        .day-view-switch-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          padding: 14px 16px;
          margin-bottom: 16px;
          background: #fff;
          border: 1px solid #e3e6ed;
          border-radius: 14px;
          box-shadow: 0 4px 16px rgba(40,45,70,.04);
        }

        .day-view-switch-copy {
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 11px;
        }

        .day-view-switch-icon {
          width: 40px;
          height: 40px;
          flex: 0 0 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          background: #eef0ff;
          color: #696cff;
          font-size: 19px;
        }

        .day-view-switch-copy h2 {
          margin: 0;
          color: #3b4050;
          font-size: 13px;
          font-weight: 750;
        }

        .day-view-switch-copy p {
          margin: 3px 0 0;
          color: #9399a8;
          font-size: 9px;
        }

        .day-view-switch {
          flex: 0 0 auto;
          display: grid;
          grid-template-columns: repeat(2, minmax(170px, 1fr));
          gap: 7px;
          padding: 4px;
          border: 1px solid #e6e8ef;
          border-radius: 11px;
          background: #f7f8fb;
        }

        .day-view-switch button {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          padding: 8px 11px;
          border: 0;
          border-radius: 8px;
          background: transparent;
          color: #777e8e;
          text-align: left;
          cursor: pointer;
          transition: .18s ease;
        }

        .day-view-switch button > i {
          font-size: 17px;
        }

        .day-view-switch button span {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .day-view-switch button strong {
          font-size: 10px;
          font-weight: 750;
        }

        .day-view-switch button small {
          margin-top: 1px;
          color: #a0a5b2;
          font-size: 8px;
        }

        .day-view-switch button.active {
          background: #696cff;
          color: #fff;
          box-shadow: 0 4px 12px rgba(105,108,255,.22);
        }

        .day-view-switch button.active small {
          color: rgba(255,255,255,.78);
        }

        .day-batch-name-cell {
          display: flex;
          align-items: center;
          gap: 9px;
          min-width: 170px;
        }

        .day-batch-icon {
          width: 34px;
          height: 34px;
          flex: 0 0 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 9px;
          background: #eef0ff;
          color: #696cff;
          font-size: 15px;
        }

        .day-batch-name-cell strong,
        .day-batch-name-cell small {
          display: block;
        }

        .day-batch-name-cell strong {
          color: #444958;
          font-size: 10px;
        }

        .day-batch-name-cell small {
          margin-top: 2px;
          color: #969cab;
          font-size: 8px;
        }

        .day-count-badge {
          min-width: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 5px 8px;
          border-radius: 7px;
          font-size: 9px;
          font-weight: 800;
        }

        .day-count-badge.total {
          background: #eef0ff;
          color: #696cff;
        }

        .day-count-badge.present {
          background: #e8f8ef;
          color: #1d9d59;
        }

        .day-count-badge.absent {
          background: #fff0f0;
          color: #df4747;
        }

        .day-count-badge.pending {
          background: #fff6e6;
          color: #c77b13;
        }

        .day-batch-progress {
          min-width: 125px;
        }

        .day-batch-progress-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 7px;
          margin-bottom: 5px;
          font-size: 8px;
        }

        .day-batch-progress-top strong {
          color: #4b5060;
          font-size: 9px;
        }

        .day-batch-progress-top span {
          color: #989eac;
        }

        .day-batch-progress-track {
          height: 6px;
          overflow: hidden;
          border-radius: 99px;
          background: #eceef3;
        }

        .day-batch-progress-track > span {
          display: block;
          height: 100%;
          border-radius: inherit;
        }

        .day-batch-progress-track > span.good {
          background: #28a968;
        }

        .day-batch-progress-track > span.average {
          background: #e6a329;
        }

        .day-batch-progress-track > span.low {
          background: #df4c4c;
        }

        .day-view-students-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 6px 9px;
          border: 1px solid #d9dcff;
          border-radius: 7px;
          background: #f7f7ff;
          color: #696cff;
          font-size: 8px;
          font-weight: 750;
          white-space: nowrap;
          cursor: pointer;
        }

        .day-view-students-btn:hover {
          background: #696cff;
          color: #fff;
        }

        @media (max-width: 900px) {
          .day-view-switch-card {
            align-items: stretch;
            flex-direction: column;
          }

          .day-view-switch {
            width: 100%;
          }
        }

        @media (max-width: 520px) {
          .day-view-switch {
            grid-template-columns: 1fr;
          }
        }

        /* ================================
           HEADER
        ================================ */

        .day-report-header {
          display: flex;

          align-items: center;

          justify-content:
            space-between;

          gap: 20px;

          padding: 22px;

          margin-bottom: 18px;

          background: #fff;

          border:
            1px solid #e2e5ec;

          border-radius: 15px;

          box-shadow:
            0 4px 16px
            rgba(40,45,70,.045);
        }

        .day-header-content {
          display: flex;

          align-items: center;

          gap: 13px;
        }

        .day-back-btn {
          width: 42px;
          height: 42px;

          display: flex;

          align-items: center;

          justify-content:
            center;

          flex: 0 0 42px;

          border:
            1px solid #dfe2e9;

          border-radius: 10px;

          background: #fff;

          color: #606571;

          font-size: 20px;

          cursor: pointer;
        }

        .day-header-icon {
          width: 50px;
          height: 50px;

          display: flex;

          align-items: center;

          justify-content:
            center;

          flex: 0 0 50px;

          border-radius: 12px;

          background: #eef0ff;

          color: #696cff;

          font-size: 24px;
        }

        .day-breadcrumb {
          display: flex;

          align-items: center;

          gap: 3px;

          margin-bottom: 4px;

          color: #9297a4;

          font-size: 12px;

          font-weight: 600;
        }

        .day-report-header h1 {
          margin: 0;

          color: #383c46;

          font-size: 25px;

          line-height: 1.2;

          font-weight: 700;
        }

        .day-report-header p {
          margin: 6px 0 0;

          color: #8c919e;

          font-size: 13px;

          line-height: 1.5;
        }

        .day-header-actions {
          display: flex;

          align-items: center;

          gap: 9px;
        }

        .day-btn {
          min-height: 42px;

          display: inline-flex;

          align-items: center;

          justify-content:
            center;

          gap: 7px;

          padding: 9px 15px;

          border-radius: 9px;

          font-size: 13px;

          font-weight: 700;

          cursor: pointer;
        }

        .day-btn-light {
          border:
            1px solid #dfe2e9;

          background: #fff;

          color: #5d626d;
        }

        .day-btn-primary {
          border:
            1px solid #696cff;

          background: #696cff;

          color: #fff;
        }

        /* ================================
           PRINT
        ================================ */

        .day-print-wrapper {
          position: relative;
        }

        .day-print-menu {
          position: absolute;

          top:
            calc(100% + 7px);

          right: 0;

          z-index: 500;

          width: 285px;

          padding: 7px;

          background: #fff;

          border:
            1px solid #e1e3ea;

          border-radius: 11px;

          box-shadow:
            0 15px 35px
            rgba(35,40,60,.15);
        }

        .day-print-menu button {
          width: 100%;

          display: flex;

          align-items: center;

          gap: 11px;

          padding: 11px;

          border: 0;

          border-radius: 8px;

          background: transparent;

          color: #4c515c;

          text-align: left;

          cursor: pointer;
        }

        .day-print-menu button:hover {
          background: #f6f7fb;
        }

        .day-print-menu button > i {
          width: 37px;
          height: 37px;

          display: flex;

          align-items: center;

          justify-content:
            center;

          border-radius: 8px;

          background: #eef0ff;

          color: #696cff;

          font-size: 18px;
        }

        .day-print-menu span {
          display: flex;

          flex-direction: column;
        }

        .day-print-menu strong {
          font-size: 13px;
        }

        .day-print-menu small {
          margin-top: 2px;

          color: #9398a5;

          font-size: 11px;
        }

        /* ================================
           URL NOTICE
        ================================ */

        .day-url-report-notice {
          display: flex;

          align-items: center;

          gap: 11px;

          padding: 12px 15px;

          margin-bottom: 15px;

          border:
            1px solid #d9dcff;

          border-radius: 10px;

          background: #f2f3ff;

          color: #5e61ca;
        }

        .day-url-report-notice > i {
          font-size: 21px;
        }

        .day-url-report-notice div {
          display: flex;

          flex-direction: column;
        }

        .day-url-report-notice strong {
          font-size: 13px;
        }

        .day-url-report-notice span {
          margin-top: 2px;

          font-size: 12px;
        }

        /* ================================
           FILTER
        ================================ */

        .day-filter-card {
          padding: 20px;

          margin-bottom: 17px;

          background: #fff;

          border:
            1px solid #e2e5ec;

          border-radius: 14px;
        }

        .day-card-heading {
          display: flex;

          align-items: center;

          justify-content:
            space-between;

          gap: 15px;

          margin-bottom: 17px;
        }

        .day-card-heading h2 {
          display: flex;

          align-items: center;

          gap: 7px;

          margin: 0;

          color: #414650;

          font-size: 16px;

          font-weight: 700;
        }

        .day-card-heading h2 i {
          color: #696cff;
        }

        .day-card-heading p {
          margin: 4px 0 0;

          color: #9398a5;

          font-size: 12px;
        }

        .day-reset-btn {
          display: inline-flex;

          align-items: center;

          gap: 5px;

          padding: 8px 11px;

          border:
            1px solid #dfe2e9;

          border-radius: 8px;

          background: #fff;

          color: #656a75;

          font-size: 12px;

          font-weight: 700;

          cursor: pointer;
        }

        .day-filter-grid {
          display: grid;

          grid-template-columns:
            repeat(
              5,
              minmax(0, 1fr)
            );

          gap: 13px;
        }

        .day-field label {
          display: block;

          margin-bottom: 6px;

          color: #565b66;

          font-size: 12px;

          font-weight: 700;
        }

        .day-control {
          position: relative;
        }

        .day-control > i {
          position: absolute;

          left: 11px;

          top: 50%;

          transform:
            translateY(-50%);

          z-index: 2;

          color: #9297a3;

          font-size: 16px;

          pointer-events: none;
        }

        .day-control select,
        .day-control input {
          width: 100%;

          height: 44px;

          padding:
            8px 10px
            8px 36px;

          border:
            1px solid #dce0e8;

          border-radius: 9px;

          outline: none;

          background: #fff;

          color: #4b505a;

          font-size: 13px;

          font-weight: 500;
        }

        .day-control select:focus,
        .day-control input:focus {
          border-color: #8f92ff;

          box-shadow:
            0 0 0 3px
            rgba(105,108,255,.09);
        }

        .day-field-help {
          display: block;

          margin-top: 5px;

          color: #969ba7;

          font-size: 10px;
        }

        .day-filter-loading {
          margin-top: 10px;

          color: #777c88;

          font-size: 12px;
        }

        /* ================================
           ACTIVE FILTERS
        ================================ */

        .day-active-filters {
          display: flex;

          flex-wrap: wrap;

          align-items: center;

          gap: 7px;

          margin-bottom: 17px;
        }

        .day-active-filters span {
          display: inline-flex;

          align-items: center;

          gap: 5px;

          padding: 7px 10px;

          border:
            1px solid #e0e3e9;

          border-radius: 7px;

          background: #fff;

          color: #656a75;

          font-size: 11px;

          font-weight: 600;
        }

        .day-active-filters
        .active-status {
          border-color: #d9dcff;

          background: #f1f2ff;

          color: #696cff;
        }

        /* ================================
           SUMMARY
        ================================ */

        .day-summary-grid {
          display: grid;

          grid-template-columns:
            repeat(4, 1fr);

          gap: 13px;

          margin-bottom: 20px;
        }

        .day-summary-card {
          min-height: 150px;

          padding: 17px;

          border:
            1px solid #e3e6ec;

          border-radius: 13px;

          background: #fff;

          text-align: left;

          cursor: pointer;

          transition: .2s ease;
        }

        .day-summary-card:hover {
          transform:
            translateY(-2px);

          box-shadow:
            0 7px 22px
            rgba(35,40,60,.07);
        }

        .day-summary-card.active.primary {
          border-color: #696cff;
        }

        .day-summary-card.active.success {
          border-color: #28a964;
        }

        .day-summary-card.active.danger {
          border-color: #dc4c4d;
        }

        .day-summary-card.active.warning {
          border-color: #e19a35;
        }

        .day-summary-header {
          display: flex;

          align-items: center;

          justify-content:
            space-between;
        }

        .day-summary-icon {
          width: 41px;
          height: 41px;

          display: flex;

          align-items: center;

          justify-content:
            center;

          border-radius: 10px;

          background: #eef0ff;

          color: #696cff;

          font-size: 20px;
        }

        .success .day-summary-icon {
          background: #eaf8f0;
          color: #28a964;
        }

        .danger .day-summary-icon {
          background: #ffeded;
          color: #dc4c4d;
        }

        .warning .day-summary-icon {
          background: #fff4e4;
          color: #d88b23;
        }

        .day-selected-badge {
          padding: 4px 7px;

          border-radius: 5px;

          background: #f2f3f7;

          color: #737884;

          font-size: 9px;

          font-weight: 700;
        }

        .day-summary-value {
          display: block;

          margin-top: 12px;

          color: #373b44;

          font-size: 29px;

          line-height: 1;
        }

        .day-summary-card h3 {
          margin:
            7px 0 0;

          color: #4c515c;

          font-size: 14px;

          font-weight: 700;
        }

        .day-summary-card p {
          margin:
            4px 0 0;

          color: #9398a5;

          font-size: 11px;
        }

        /* ================================
           TABLE
        ================================ */

        .day-table-card {
          overflow: hidden;

          background: #fff;

          border:
            1px solid #e2e5ec;

          border-radius: 14px;
        }

        .day-table-header {
          display: flex;

          align-items: center;

          justify-content:
            space-between;

          gap: 15px;

          padding: 17px;

          border-bottom:
            1px solid #eceef3;
        }

        .day-table-header h2 {
          margin: 0;

          color: #414650;

          font-size: 17px;

          font-weight: 700;
        }

        .day-table-header p {
          margin: 4px 0 0;

          color: #9297a4;

          font-size: 12px;
        }

        .day-table-actions {
          display: flex;

          align-items: center;

          gap: 8px;
        }

        .day-mini-select,
        .day-search {
          position: relative;
        }

        .day-mini-select i,
        .day-search > i {
          position: absolute;

          left: 10px;

          top: 50%;

          transform:
            translateY(-50%);

          color: #9297a4;

          font-size: 15px;

          pointer-events: none;
        }

        .day-mini-select select {
          height: 40px;

          min-width: 145px;

          padding:
            7px 10px
            7px 31px;

          border:
            1px solid #dfe2e9;

          border-radius: 8px;

          background: #fff;

          color: #555a65;

          font-size: 12px;
        }

        .day-search input {
          width: 270px;

          height: 40px;

          padding:
            7px 33px
            7px 32px;

          border:
            1px solid #dfe2e9;

          border-radius: 8px;

          outline: none;

          color: #4f545e;

          font-size: 12px;
        }

        .day-search button {
          position: absolute;

          right: 5px;

          top: 50%;

          transform:
            translateY(-50%);

          border: 0;

          background:
            transparent;

          color: #999;

          cursor: pointer;
        }

        .day-table-print {
          height: 40px;

          display: inline-flex;

          align-items: center;

          gap: 6px;

          padding:
            7px 12px;

          border:
            1px solid #dfe2e9;

          border-radius: 8px;

          background: #fff;

          color: #5f646f;

          font-size: 12px;

          font-weight: 700;

          cursor: pointer;
        }

        .day-table-scroll {
          width: 100%;

          overflow-x: auto;
        }

        .day-table {
          width: 100%;

          min-width: 1000px;

          border-collapse:
            collapse;
        }

        .day-table th {
          padding:
            12px 13px;

          background: #f7f8fa;

          border-bottom:
            1px solid #e3e6ec;

          color: #6c717d;

          font-size: 11px;

          font-weight: 700;

          text-align: left;

          text-transform:
            uppercase;

          white-space: nowrap;
        }

        .day-table td {
          padding:
            12px 13px;

          border-bottom:
            1px solid #eef0f3;

          color: #555a65;

          font-size: 12px;

          vertical-align:
            middle;
        }

        .day-table tbody tr:hover {
          background: #fafbfc;
        }

        .day-table .number {
          width: 48px;

          text-align: center;
        }

        .day-student {
          display: flex;

          align-items: center;

          gap: 9px;
        }

        .day-avatar {
          width: 36px;
          height: 36px;

          display: flex;

          align-items: center;

          justify-content:
            center;

          flex: 0 0 36px;

          border-radius: 50%;

          background: #eef0ff;

          color: #696cff;

          font-size: 10px;

          font-weight: 700;
        }

        .day-student > div:last-child {
          display: flex;

          flex-direction: column;
        }

        .day-student strong {
          color: #424751;

          font-size: 12.5px;
        }

        .day-student span {
          margin-top: 2px;

          color: #969ba7;

          font-size: 10px;
        }

        .day-reg {
          font-weight: 600;
        }

        .day-batch {
          display: inline-block;

          padding: 5px 8px;

          border-radius: 6px;

          background: #f1f2f5;

          font-size: 11px;

          font-weight: 600;
        }

        .day-status {
          display: inline-flex;

          align-items: center;

          gap: 5px;

          padding: 5px 8px;

          border-radius: 6px;

          font-size: 11px;

          font-weight: 700;

          white-space: nowrap;
        }

        .attendance-status-present {
          background: #e9f8ef;

          color: #239557;
        }

        .attendance-status-absent {
          background: #ffeded;

          color: #d43f40;
        }

        .attendance-status-pending {
          background: #fff4e3;

          color: #c87c19;
        }

        .day-empty {
          padding:
            50px 20px !important;

          text-align: center;
        }

        .day-empty i {
          display: block;

          margin-bottom: 8px;

          color: #a2a6b0;

          font-size: 32px;
        }

        .day-empty strong {
          display: block;

          color: #555a64;

          font-size: 14px;
        }

        .day-empty span {
          display: block;

          margin-top: 4px;

          color: #969ba6;

          font-size: 12px;
        }

        .day-spin {
          display: inline-block;

          animation:
            daySpin 1s
            linear infinite;
        }

        @keyframes daySpin {
          to {
            transform:
              rotate(360deg);
          }
        }

        /* ================================
           RESPONSIVE
        ================================ */

        @media
        (max-width: 1250px) {

          .day-filter-grid {
            grid-template-columns:
              repeat(3, 1fr);
          }

          .day-table-header {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .day-table-actions {
            width: 100%;
          }

          .day-search {
            flex: 1;
          }

          .day-search input {
            width: 100%;
          }

        }

        @media
        (max-width: 900px) {

          .day-filter-grid {
            grid-template-columns:
              repeat(2, 1fr);
          }

          .day-summary-grid {
            grid-template-columns:
              repeat(2, 1fr);
          }

        }

        @media
        (max-width: 767px) {

          .day-content-container {
            padding:
              10px 10px
              28px;
          }

          .day-master-wrapper {
            margin-bottom: 12px;
          }

          .day-report-main {
            padding: 0;
          }

          .day-report-header {
            align-items:
              flex-start;

            flex-direction:
              column;

            padding: 15px;
          }

          .day-header-icon {
            display: none;
          }

          .day-report-header h1 {
            font-size: 20px;
          }

          .day-report-header p {
            font-size: 12px;
          }

          .day-header-actions {
            width: 100%;
          }

          .day-header-actions
          > * {
            flex: 1;
          }

          .day-btn {
            width: 100%;
          }

          .day-filter-card {
            padding: 14px;
          }

          .day-filter-grid {
            grid-template-columns:
              1fr;
          }

          .day-control select,
          .day-control input {
            height: 46px;

            font-size: 14px;
          }

          .day-field label {
            font-size: 13px;
          }

          .day-summary-grid {
            gap: 8px;
          }

          .day-summary-card {
            min-height: 145px;

            padding: 14px;
          }

          .day-summary-value {
            font-size: 26px;
          }

          .day-summary-card h3 {
            font-size: 13px;
          }

          .day-table-actions {
            align-items:
              stretch;

            flex-direction:
              column;
          }

          .day-mini-select select,
          .day-search input,
          .day-table-print {
            width: 100%;

            min-width: 0;
          }

          .day-table th {
            font-size: 11px;
          }

          .day-table td {
            font-size: 12px;
          }

        }

        @media
        (max-width: 430px) {

          .day-summary-grid {
            grid-template-columns:
              1fr 1fr;
          }

          .day-summary-card {
            min-height: 138px;
          }

          .day-summary-icon {
            width: 36px;
            height: 36px;

            font-size: 17px;
          }

          .day-summary-value {
            font-size: 23px;
          }

        }

      
        /* SCAI ATTENDANCE: flush navbar fix, isolated to this page.
           The global template otherwise adds padding above .layout-page. */
        html.layout-navbar-fixed .layout-wrapper.day-attendance-page:not(.layout-without-menu) > .layout-container > .layout-page,
        html.layout-navbar-fixed .layout-wrapper.day-attendance-page .layout-page,
        .layout-wrapper.day-attendance-page > .layout-container > .layout-page {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        .layout-wrapper.day-attendance-page > .layout-container {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        .layout-wrapper.day-attendance-page .layout-page > .no-print:first-child {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        .layout-wrapper.day-attendance-page #layout-navbar.scai-topbar {
          top: 0 !important;
          margin: 0 !important;
          width: 100% !important;
          max-width: none !important;
          border-radius: 0 !important;
        }
`}</style>
    </>
  );
}
